import { ArrowRight, BadgeCheck, CalendarClock, FileCheck2, ReceiptText } from "lucide-react";
import { Link } from "react-router-dom";

const STEPS = [
  {
    icon: BadgeCheck,
    label: "Sales",
    title: "Close the deal",
    text: "Keep customer details and the sales opportunity together.",
  },
  {
    icon: CalendarClock,
    label: "Projects · Planning · Time tracking",
    title: "Run the work",
    text: "Plan the project, assign tasks and record billable hours.",
  },
  {
    icon: ReceiptText,
    label: "Finance & Invoicing",
    title: "Prepare the invoice",
    text: "Turn project hours into a draft invoice you review before sending.",
  },
];

export default function WorkflowSection() {
  return (
    <section
      aria-labelledby="connected-workflow-title"
      data-testid="connected-workflow"
      className="zy-section bg-[#FAFAFB]"
    >
      <div className="max-w-5xl mx-auto px-6">
        <div className="max-w-3xl">
          <p className="zy-eyebrow mb-4">One connected workflow</p>
          <h2 id="connected-workflow-title" className="zy-h2">
            Run your projects. Send your invoices. Win back your time.
          </h2>
          <p className="zy-body mt-5">
            From the first customer conversation to project planning and a ready-to-review invoice, Zynthoro keeps everything connected — without copying the same information between separate tools.
          </p>
        </div>

        <ol className="mt-10 grid gap-4 md:grid-cols-3" aria-label="Zynthoro workflow steps">
          {STEPS.map((step, index) => {
            const Icon = step.icon;
            return (
              <li
                key={step.title}
                className="relative rounded-2xl border border-[#e8ebf2] bg-white p-6 shadow-sm"
                data-testid={`workflow-step-${index + 1}`}
              >
                <div className="flex items-center justify-between gap-4">
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#202B60]/10 text-[#202B60]">
                    <Icon size={20} aria-hidden="true" />
                  </span>
                  <span className="text-[12px] font-bold text-[#202B60]/60">0{index + 1}</span>
                </div>
                <p className="mt-5 text-[11px] font-bold uppercase tracking-[0.12em] text-[#806916]">
                  {step.label}
                </p>
                <h3 className="mt-2 text-xl font-bold text-[#0A1628]">{step.title}</h3>
                <p className="mt-3 text-[14px] leading-6 text-slate-600">{step.text}</p>
                {index < STEPS.length - 1 && (
                  <ArrowRight
                    size={18}
                    aria-hidden="true"
                    className="absolute -right-3 top-1/2 z-10 hidden -translate-y-1/2 text-[#D4AF37] md:block"
                  />
                )}
              </li>
            );
          })}
        </ol>

        <div className="mt-8 flex flex-col gap-4 rounded-xl border border-[#dfe5f2] bg-white px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
          <p className="flex items-center gap-2 text-[13.5px] font-semibold text-[#0A1628]">
            <FileCheck2 size={17} className="text-[#202B60]" aria-hidden="true" />
            You review every step before the invoice is sent.
          </p>
          <Link to="/modules" className="inline-flex items-center gap-1.5 text-[13.5px] font-bold text-[#202B60] hover:underline">
            Explore the connected modules <ArrowRight size={15} aria-hidden="true" />
          </Link>
        </div>
      </div>
    </section>
  );
}
